import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Category {
  id: string;
  name: string;
  slug: string;
  icon: string;
  description: string;
}

// Default icons by slug — used by UI components for visual display
const CATEGORY_ICONS: Record<string, string> = {
  laptops: "💻",
  desktops: "🖥️",
  mobiles: "📱",
  printers: "🖨️",
  accessories: "🎧",
  monitors: "🖥️",
  networking: "🌐",
};

export function useCategories() {
  const query = useQuery<Category[]>({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_categories")
        .select("id, name, slug, icon, description")
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
    /* Categories rarely change — cache for 5 minutes, realtime handles invalidation */
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });

  return query;
}

/** Subscribe to realtime category changes — call once at app level or per page */
export function useCategoriesRealtime() {
  const qc = useQueryClient();

  useEffect(() => {
    const channel = supabase
      .channel("categories-realtime")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "product_categories" },
        () => {
          qc.invalidateQueries({ queryKey: ["categories"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [qc]);
}

/** Get the emoji icon for a category — prefers DB value, falls back to slug map */
export function getCategoryIcon(slug: string, dbIcon?: string): string {
  if (dbIcon) return dbIcon;
  return CATEGORY_ICONS[slug] ?? "📦";
}
